import React, { useState } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import { useConsultationModal } from '../context/ConsultationModalContext';

// Futuristic SVG Icons for Security
const IconZeroTrust = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
        <path d="M12 3V7M12 17V21M3 12H7M17 12H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M5.64 5.64L8.17 8.17M15.83 15.83L18.36 18.36M5.64 18.36L8.17 15.83M15.83 8.17L18.36 5.64" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconFirewall = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 10H21" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M7 7H9M7 14H9M7 17H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M12 14L14 16L18 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const IconMicroseg = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 6.5H14M6.5 10V14M17.5 10V14M10 17.5H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 2"/>
    </svg>
);

const IconIAM = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M4 20C4 16.686 7.582 14 12 14C16.418 14 20 16.686 20 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M15 8L17 10L21 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const IconPAM = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 14V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="15" r="1" fill="currentColor"/>
        <path d="M8 10V7C8 4.791 9.791 3 12 3C14.209 3 16 4.791 16 7V10" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 6V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconWorkload = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path d="M12 2L20 7V17L12 22L4 17V7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M12 22V12M12 12L4 7M12 12L20 7" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
);

const IconMonitoring = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M7 11L10 8L13 11L17 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="17" cy="7" r="1.5" fill="currentColor"/>
        <path d="M8 21H16M12 18V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconCompliance = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path d="M9 3H5C3.895 3 3 3.895 3 5V19C3 20.105 3.895 21 5 21H19C20.105 21 21 20.105 21 19V5C21 3.895 20.105 3 19 3H15" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="9" y="1" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M8 10L11 13L16 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8 16H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const Security = () => {
    const [activeSection, setActiveSection] = useState('zerotrust');
    const { openModal } = useConsultationModal();

    const sections = [
        { id: 'zerotrust', label: 'Zero Trust', Icon: IconZeroTrust },
        { id: 'network', label: 'Network Security', Icon: IconFirewall },
        { id: 'microseg', label: 'Microsegmentation', Icon: IconMicroseg },
        { id: 'iam', label: 'Identity & Access', Icon: IconIAM },
        { id: 'pam', label: 'Privileged Access', Icon: IconPAM },
        { id: 'workload', label: 'Workload Security', Icon: IconWorkload },
        { id: 'monitoring', label: 'Monitoring', Icon: IconMonitoring },
        { id: 'compliance', label: 'Compliance', Icon: IconCompliance },
    ];

    return (
        <Layout>
            <SEO page="security" />
            <PageHeader
                title="Enterprise Security" 
                highlight="& Identity"
                description="Security at EverestCloud is not a feature—it is the foundation. Designed for Nepal's most regulated industries, our platform follows a Zero Trust, defense-in-depth security architecture, ensuring that every identity, workload, network flow, and access request is continuously verified."
            />

            <section className="py-16 bg-white dark:bg-slate-950 transition-colors duration-500">
                <div className="container mx-auto px-4">

                    {/* Section Navigation */}
                    <div className="flex flex-wrap justify-center gap-2 mb-12">
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => setActiveSection(section.id)}
                                data-testid={`security-tab-${section.id}`}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                                    activeSection === section.id
                                        ? 'bg-accent text-black shadow-lg'
                                        : 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                            >
                                <div className="w-4 h-4">
                                    <section.Icon />
                                </div>
                                {section.label}
                            </button>
                        ))}
                    </div>

                    {/* Content Sections */}
                    <div className="space-y-8">
                        
                        {/* Zero Trust */}
                        {activeSection === 'zerotrust' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <SecurityCard
                                    Icon={IconZeroTrust}
                                    title="Zero Trust by Design"
                                    subtitle="Never Trust. Always Verify."
                                    description="EverestCloud enforces Zero Trust architecture by default, ensuring that no user, workload, or network segment is implicitly trusted—whether inside or outside the platform. Every request is authenticated, authorized, inspected, and logged—eliminating traditional perimeter assumptions."
                                    leftTitle="Core Zero Trust Principles"
                                    leftItems={[
                                        "Identity-first access validation",
                                        "Continuous authentication and authorization",
                                        "Least-privilege enforcement",
                                        "Micro-segmented network boundaries",
                                        "Real-time threat inspection"
                                    ]}
                                    rightTitle="Security Guarantees"
                                    rightItems={[
                                        "No implicit trust for any entity",
                                        "Every session continuously validated",
                                        "Complete audit trail of all access",
                                        "Automatic policy enforcement"
                                    ]}
                                />

                                {/* Zero Trust Visual - Unified Timeline */}
                                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-white/5 p-8 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-accent/5 rounded-full blur-[100px] pointer-events-none"></div>
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-8 text-center">Continuous Verification Process</h3>
                                    <div className="grid md:grid-cols-5 gap-4 relative z-10">
                                        <FlowStep number="1" title="Verify Identity" desc="Every user & device authenticated" color="gold" />
                                        <FlowStep number="2" title="Validate Context" desc="Location, device, behavior checked" color="gold" />
                                        <FlowStep number="3" title="Enforce Policy" desc="Least privilege applied" color="gold" />
                                        <FlowStep number="4" title="Inspect Traffic" desc="Real-time threat analysis" color="gold" />
                                        <FlowStep number="5" title="Log & Monitor" desc="Continuous visibility" color="gold" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Network Security */}
                        {activeSection === 'network' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <SecurityCard
                                    Icon={IconFirewall}
                                    title="Network Security & Traffic Protection"
                                    subtitle="Intelligent Defense at the Edge and Core"
                                    description="Our network perimeter and internal fabric are protected by enterprise-grade next-generation firewalls, delivering deep visibility and active threat prevention."
                                    leftTitle="Security Capabilities"
                                    leftItems={[
                                        "Application-aware firewalling",
                                        "Intrusion Prevention System (IPS)",
                                        "Advanced malware detection",
                                        "URL and DNS threat filtering",
                                        "Encrypted traffic inspection",
                                        "Stateful and policy-driven access control"
                                    ]}
                                    rightTitle="DDoS Protection"
                                    rightItems={[
                                        "Built-in volumetric DDoS mitigation",
                                        "Automatic anomaly detection",
                                        "Real-time threat response",
                                        "Uninterrupted service delivery"
                                    ]}
                                />

                                <div className="grid md:grid-cols-3 gap-6">
                                    <SecurityFeatureCard 
                                        title="Next-Gen Firewall" 
                                        desc="Deep packet inspection with application awareness and threat intelligence integration"
                                        accent="gold"
                                    />
                                    <SecurityFeatureCard 
                                        title="IPS/IDS" 
                                        desc="Real-time intrusion detection and prevention with automated threat response"
                                        accent="blue"
                                    />
                                    <SecurityFeatureCard 
                                        title="DDoS Shield" 
                                        desc="Volumetric attack mitigation ensuring business continuity under attack"
                                        accent="purple"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Microsegmentation */}
                        {activeSection === 'microseg' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <SecurityCard
                                    Icon={IconMicroseg}
                                    title="Microsegmentation"
                                    subtitle="Stop Lateral Movement Before It Starts"
                                    description="Traditional flat networks allow threats to spread. EverestCloud uses fine-grained microsegmentation to isolate workloads and restrict east-west traffic. Each workload communicates only with what it is explicitly allowed to—nothing more."
                                    leftTitle="Key Benefits"
                                    leftItems={[
                                        "Prevents lateral threat propagation",
                                        "Enforces workload-level security policies",
                                        "Isolates PCI, non-PCI, and sensitive workloads",
                                        "Enables environment-specific segmentation"
                                    ]}
                                    rightTitle="Segmentation Zones"
                                    rightTags={["Production", "Disaster Recovery", "Test/Dev", "DMZ", "Management", "PCI Zone"]}
                                />

                                {/* Microsegmentation Visual - Unified Style */}
                                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-white/5 p-8 relative overflow-hidden">
                                    <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-[100px] pointer-events-none"></div>
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-8 text-center">Workload Isolation Model</h3>
                                    <div className="grid md:grid-cols-4 gap-4 relative z-10">
                                        <IsolationCard title="Web Tier" items={["Load Balancers", "Web Servers", "CDN Edge"]} color="blue" />
                                        <IsolationCard title="Application Tier" items={["API Gateway", "App Servers", "Microservices"]} color="gold" />
                                        <IsolationCard title="Data Tier" items={["Databases", "Cache Layers", "File Storage"]} color="purple" />
                                        <IsolationCard title="Management" items={["Admin Consoles", "Monitoring", "Backup Systems"]} color="green" />
                                    </div>
                                    <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-6">
                                        Each segment enforces strict policies—cross-segment traffic requires explicit authorization
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Identity & Access Management */}
                        {activeSection === 'iam' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <SecurityCard
                                    Icon={IconIAM}
                                    title="Identity & Access Management (IAM)"
                                    subtitle="Identity Is the New Perimeter"
                                    description="Access to EverestCloud resources is governed by a centralized Identity & Access Management framework, tightly integrated across the platform."
                                    leftTitle="Key Capabilities"
                                    leftItems={[
                                        "Centralized user and service identity management",
                                        "Role-Based Access Control (RBAC)",
                                        "Tenant-level isolation",
                                        "Granular permissions for all resources",
                                        "Full audit trail of access events"
                                    ]}
                                    rightTitle="Multi-Factor Authentication"
                                    rightItems={[
                                        "Mandatory MFA for all management access",
                                        "Hardware token support",
                                        "Authenticator app integration",
                                        "Significantly reduced credential risk"
                                    ]}
                                />

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="bg-white dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-white/10 p-6">
                                        <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                                            <span className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center text-accent">
                                                <IconIAM />
                                            </span>
                                            Access Control Model
                                        </h4>
                                        <div className="space-y-3">
                                            <RBACRow role="Tenant Admin" access="Full tenant management" />
                                            <RBACRow role="Project Owner" access="Project resources & users" />
                                            <RBACRow role="Developer" access="Compute, storage, network" />
                                            <RBACRow role="Read Only" access="View-only access" />
                                            <RBACRow role="Billing Admin" access="Usage & invoicing" />
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-white/10 p-6">
                                        <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                                            <span className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500">
                                                <IconZeroTrust />
                                            </span>
                                            MFA Enforcement
                                        </h4>
                                        <ul className="space-y-3">
                                            <MFAItem text="Console & Portal Access" required />
                                            <MFAItem text="API Management Operations" required />
                                            <MFAItem text="Privileged Actions" required />
                                            <MFAItem text="Service Account Operations" optional />
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Privileged Access Management */}
                        {activeSection === 'pam' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <SecurityCard
                                    Icon={IconPAM}
                                    title="Privileged Access Management (PAM)"
                                    subtitle="Secure the Most Powerful Accounts"
                                    description="Privileged credentials are the highest-value targets. EverestCloud secures them through a dedicated Privileged Access Management vault."
                                    leftTitle="PAM Capabilities"
                                    leftItems={[
                                        "Secure storage of privileged credentials",
                                        "Session recording and activity monitoring",
                                        "Password rotation and credential lifecycle",
                                        "Policy-driven approval workflows",
                                        "Complete audit trail"
                                    ]}
                                    rightTitle="Just-In-Time Access"
                                    rightItems={[
                                        "Zero Standing Privileges enforced",
                                        "Access granted only when required",
                                        "Limited duration access windows",
                                        "Automatic revocation",
                                        "Drastically reduced attack surface"
                                    ]}
                                />

                                {/* PAM Workflow Visual - Unified Style */}
                                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-white/5 p-8 relative overflow-hidden">
                                    <div className="absolute top-0 left-0 w-64 h-64 bg-accent/5 rounded-full blur-[100px] pointer-events-none"></div>
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-8 text-center relative z-10">Just-In-Time Access Workflow</h3>
                                    <div className="grid md:grid-cols-4 gap-6 relative z-10">
                                        <FlowStep number="1" title="Request" desc="User requests privileged access" color="blue" />
                                        <FlowStep number="2" title="Approve" desc="Policy-driven approval workflow" color="blue" />
                                        <FlowStep number="3" title="Grant" desc="Time-limited access provided" color="blue" />
                                        <FlowStep number="4" title="Revoke" desc="Automatic access expiration" color="blue" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Platform & Workload Security */}
                        {activeSection === 'workload' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <SecurityCard
                                    Icon={IconWorkload}
                                    title="Platform & Workload Security"
                                    subtitle="Secure by Architecture, Not Add-Ons"
                                    description="Security is embedded at every layer of the EverestCloud platform. Workloads inherit security policies automatically—ensuring consistency across environments and regions."
                                    leftTitle="Platform Security"
                                    leftItems={[
                                        "Secure compute isolation",
                                        "Encrypted storage and data paths",
                                        "Secure workload orchestration",
                                        "Policy-driven container isolation",
                                        "Continuous configuration compliance"
                                    ]}
                                    rightTitle="Workload Protection"
                                    rightItems={[
                                        "Automatic security policy inheritance",
                                        "Runtime threat detection",
                                        "Vulnerability scanning",
                                        "Immutable infrastructure support"
                                    ]}
                                />
                            </div>
                        )}

                        {/* Monitoring, Logging & Incident Readiness */}
                        {activeSection === 'monitoring' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <SecurityCard
                                    Icon={IconMonitoring}
                                    title="Monitoring, Logging & Incident Readiness"
                                    subtitle="Visibility Without Blind Spots"
                                    description="EverestCloud provides continuous monitoring and centralized logging across infrastructure, platform, and access layers. These capabilities support real-time threat detection, forensic analysis, and regulatory audits."
                                    leftTitle="Security Visibility"
                                    leftItems={[
                                        "Authentication and authorization logs",
                                        "Network flow inspection",
                                        "Privileged session activity",
                                        "Configuration and policy changes",
                                        "Security event correlation"
                                    ]}
                                    rightTitle="Incident Response"
                                    rightItems={[
                                        "Real-time alerting",
                                        "Automated threat response",
                                        "Forensic data collection",
                                        "Incident timeline reconstruction"
                                    ]}
                                />

                                {/* Monitoring Dashboard Visual */}
                                <div className="bg-slate-900 rounded-2xl border border-white/10 p-6 relative overflow-hidden">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="flex gap-1.5">
                                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                        </div>
                                        <span className="text-white/60 text-sm font-mono">soc.everestcloud.com.np</span>
                                    </div>
                                    <div className="grid md:grid-cols-4 gap-4">
                                        <MetricCard title="Auth Events" value="12.4K" change="+2.3%" status="normal" />
                                        <MetricCard title="Blocked Threats" value="847" change="+12%" status="warning" />
                                        <MetricCard title="Active Sessions" value="1,203" change="-5%" status="normal" />
                                        <MetricCard title="Security Score" value="98.2%" change="+0.5%" status="good" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Compliance */}
                        {activeSection === 'compliance' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <SecurityCard
                                    Icon={IconCompliance}
                                    title="Compliance-Ready by Design"
                                    subtitle="Built for Audits, Not Retrofits"
                                    description="EverestCloud is engineered to support the compliance requirements of banking & financial institutions, government agencies, telecom operators, and regulated enterprises."
                                    leftTitle="Compliance Capabilities"
                                    leftItems={[
                                        "Full audit logging and traceability",
                                        "Strong tenant isolation",
                                        "Data residency enforcement within Nepal",
                                        "Security controls aligned with global best practices",
                                        "Support for internal and external audits"
                                    ]}
                                    rightTitle="Industry Support"
                                    rightTags={["Banking & BFSI", "Government", "Telecom", "Healthcare", "Utilities", "Enterprise"]}
                                />

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-white/10 p-6">
                                        <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Data Sovereignty Assurance</h4>
                                        <ul className="space-y-3">
                                            <ComplianceItem text="All data physically stored in Nepal" />
                                            <ComplianceItem text="No foreign jurisdiction exposure" />
                                            <ComplianceItem text="Local legal recourse available" />
                                            <ComplianceItem text="NRB directive compliance" />
                                        </ul>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-white/10 p-6">
                                        <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Audit Readiness</h4>
                                        <ul className="space-y-3">
                                            <ComplianceItem text="Complete access audit trails" />
                                            <ComplianceItem text="Configuration change history" />
                                            <ComplianceItem text="Security event logs" />
                                            <ComplianceItem text="Compliance reporting tools" />
                                        </ul>
                                    </div>
                                </div>

                                {/* Trust Banner */}
                                <div className="bg-gradient-to-r from-accent/10 via-accent/5 to-transparent rounded-2xl border border-accent/20 p-8 text-center">
                                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">Security That Enables Trust</h3>
                                    <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-6">
                                        EverestCloud delivers enterprise-grade security with sovereign control, enabling organizations to innovate confidently while meeting regulatory and operational demands.
                                    </p>
                                    <div className="flex flex-wrap justify-center gap-4">
                                        <TrustBadge text="Zero Trust Architecture" />
                                        <TrustBadge text="Defense-in-Depth Stack" />
                                        <TrustBadge text="Identity-Centric Access" />
                                        <TrustBadge text="100% Nepal Sovereignty" />
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-20 text-center">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                            Ready to Secure Your Infrastructure?
                        </h2>
                        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-8">
                            Talk to our security architects about how EverestCloud protects Nepal&apos;s most critical workloads.
                        </p>
                        <button 
                            onClick={() => openModal('security_assessment')}
                            data-testid="security-cta-button"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-black font-bold rounded-full hover:bg-accent/90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
                        >
                            Request Security Assessment
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                </div>
            </section>
        </Layout>
    );
};

// Component: Main Security Card
const SecurityCard = ({ Icon, title, subtitle, description, leftTitle, leftItems = [], rightTitle, rightItems = [], rightTags = [] }) => (
    <div className="bg-white dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-white/10 p-8 relative overflow-hidden group hover:border-accent/30 transition-all duration-300">
        <div className="absolute top-0 right-0 w-48 h-48 bg-accent/5 rounded-full blur-[80px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"></div>
        
        <div className="flex flex-col md:flex-row gap-8 relative z-10">
            {/* Left - Header & Description */}
            <div className="md:w-1/3">
                <div className="w-14 h-14 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent mb-4">
                    <div className="w-7 h-7">
                        <Icon />
                    </div>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{title}</h3>
                <p className="text-accent font-medium text-sm uppercase tracking-wider mb-4">{subtitle}</p>
                {description && (
                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{description}</p>
                )}
            </div>

            {/* Right - Details */}
            <div className="md:w-2/3 grid md:grid-cols-2 gap-8">
                {leftTitle && (
                    <div>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                            {leftTitle}
                        </h4>
                        <ul className="space-y-2.5">
                            {leftItems.map((item, i) => (
                                <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-300 text-sm">
                                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {rightTitle && (
                    <div>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                            {rightTitle}
                        </h4>
                        {rightItems.length > 0 && (
                            <ul className="space-y-2.5">
                                {rightItems.map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-300 text-sm">
                                        <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {rightTags.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {rightTags.map((tag, i) => (
                                    <span key={i} className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-medium rounded-full">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    </div>
);

// Component: Unified Flow Step (for Zero Trust & PAM workflows)
const FlowStep = ({ number, title, desc, color = "gold" }) => {
    const colors = {
        gold: { bg: 'bg-accent/10', border: 'border-accent/30', text: 'text-accent' },
        blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-500' }
    };
    const c = colors[color];
    
    return (
        <div className="text-center relative z-10">
            <div className={`w-14 h-14 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center mx-auto mb-3`}>
                <span className={`${c.text} font-bold text-xl`}>{number}</span>
            </div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-1">{title}</h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
        </div>
    );
};

// Component: Isolation Card (for Microsegmentation)
const IsolationCard = ({ title, items, color }) => {
    const styles = {
        blue: { border: 'border-blue-500/30 hover:border-blue-500/50', badge: 'bg-blue-500/10 text-blue-500' },
        gold: { border: 'border-accent/30 hover:border-accent/50', badge: 'bg-accent/10 text-accent' },
        purple: { border: 'border-purple-500/30 hover:border-purple-500/50', badge: 'bg-purple-500/10 text-purple-500' },
        green: { border: 'border-green-500/30 hover:border-green-500/50', badge: 'bg-green-500/10 text-green-500' }
    };
    const s = styles[color];
    
    return (
        <div className={`bg-white dark:bg-slate-900/60 rounded-xl border ${s.border} p-5 transition-all duration-300`}>
            <div className={`${s.badge} rounded-lg px-3 py-1.5 mb-4 w-fit`}>
                <span className="text-xs font-bold uppercase tracking-wider">{title}</span>
            </div>
            <ul className="space-y-2">
                {items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <div className="w-1 h-1 rounded-full bg-slate-400"></div>
                        {item}
                    </li>
                ))}
            </ul>
        </div>
    );
};

// Component: Security Feature Card (for Network Security)
const SecurityFeatureCard = ({ title, desc, accent }) => {
    const styles = {
        gold: { border: 'border-accent/30 hover:border-accent/50', dot: 'bg-accent' },
        blue: { border: 'border-blue-500/30 hover:border-blue-500/50', dot: 'bg-blue-500' },
        purple: { border: 'border-purple-500/30 hover:border-purple-500/50', dot: 'bg-purple-500' }
    };
    const s = styles[accent];
    
    return (
        <div className={`bg-white dark:bg-slate-900/60 rounded-xl border ${s.border} p-6 transition-all duration-300`}>
            <div className="flex items-center gap-2 mb-3">
                <div className={`w-2 h-2 rounded-full ${s.dot}`}></div>
                <h4 className="font-bold text-slate-900 dark:text-white">{title}</h4>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">{desc}</p>
        </div>
    );
};

// Component: RBAC Row
const RBACRow = ({ role, access }) => (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-white/5 last:border-0">
        <span className="font-medium text-slate-900 dark:text-white text-sm">{role}</span>
        <span className="text-slate-500 dark:text-slate-400 text-sm">{access}</span>
    </div>
);

// Component: MFA Item
const MFAItem = ({ text, required }) => (
    <li className="flex items-center justify-between">
        <span className="text-slate-600 dark:text-slate-300 text-sm">{text}</span>
        <span className={`text-xs font-bold px-2 py-1 rounded ${required ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
            {required ? 'Required' : 'Optional'}
        </span>
    </li>
);

// Component: Metric Card
const MetricCard = ({ title, value, change, status }) => {
    const statusColors = {
        normal: 'text-slate-400',
        good: 'text-green-400',
        warning: 'text-yellow-400'
    };
    
    return (
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{title}</p>
            <p className="text-2xl font-bold text-white mb-1">{value}</p>
            <p className={`text-xs ${statusColors[status]}`}>{change} from last week</p>
        </div>
    );
};

// Component: Compliance Item
const ComplianceItem = ({ text }) => (
    <li className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
        <span className="text-sm">{text}</span>
    </li>
);

// Component: Trust Badge
const TrustBadge = ({ text }) => (
    <span className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-full text-sm font-medium text-slate-700 dark:text-slate-300 shadow-sm">
        {text}
    </span>
);

export default Security;
